import { Helmet } from "react-helmet-async";
import {
    SITE_URL,
    SITE_NAME,
    SITE_DEFAULT_TITLE,
    SITE_DEFAULT_DESCRIPTION,
    OG_IMAGE_PATH,
} from "@/lib/site";
import { formatPageTitle } from "@/lib/seo";

function absoluteUrl(path = "/") {
    if (!path) return SITE_URL;
    if (path.startsWith("http")) return path;
    return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Per-page head tags + optional JSON-LD graph.
 */
export default function Seo({
    title,
    description = SITE_DEFAULT_DESCRIPTION,
    path = "/",
    image = OG_IMAGE_PATH,
    type = "website",
    noindex = false,
    jsonLd,
}) {
    const fullTitle = formatPageTitle(title) || SITE_DEFAULT_TITLE;
    const canonical = absoluteUrl(path);
    const imageUrl = absoluteUrl(image);
    const robots = noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    const graph = jsonLd
        ? Array.isArray(jsonLd)
            ? jsonLd
            : [jsonLd]
        : [];

    return (
        <Helmet prioritizeSeoTags>
            <html lang="fr" />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="robots" content={robots} />
            <meta name="author" content="Joëlle Berthaud" />
            <meta name="geo.region" content="FR-73" />
            <meta name="geo.placename" content="Challes-les-Eaux" />
            <link rel="canonical" href={canonical} />
            <link rel="describedby" href={`${SITE_URL}/llms.txt`} />

            <meta property="og:type" content={type} />
            <meta property="og:locale" content="fr_FR" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:alt" content={fullTitle} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />

            {graph.map((node, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(node)}
                </script>
            ))}
        </Helmet>
    );
}
